<mat-drawer-container class="h-full" autosize>
    <!-- Main content (passthrough) -->
    <mat-drawer-content>
        <ng-content></ng-content>
    </mat-drawer-content>

    <!-- IP Analysis Drawer -->
    <mat-drawer #drawer [(opened)]="isOpen" (openedChange)="onDrawerOpenedChange($event)" mode="over" position="end"
        class="ip-analysis-drawer">
        <div class="flex flex-col h-full">
            <!-- Header -->
            <div class="flex items-center justify-between p-6 border-b bg-card sticky top-0 z-10">
                <div class="flex items-center gap-3">
                    <mat-icon class="text-primary">info</mat-icon>
                    <div>
                        <h2 class="text-xl font-bold">Análisis de IP</h2>
                        <p class="text-sm text-secondary" *ngIf="currentIP">{{ currentIP }}</p>
                    </div>
                </div>
                <button mat-icon-button (click)="close()" matTooltip="Cerrar">
                    <mat-icon>close</mat-icon>
                </button>
            </div>

            <!-- Content -->
            <div class="flex-1 overflow-y-auto p-6">
                <!-- Loading State -->
                <div *ngIf="loading" class="flex flex-col items-center justify-center py-12">
                    <mat-spinner diameter="60"></mat-spinner>
                    <p class="mt-4 text-lg text-secondary">Consultando bases de datos...</p>
                </div>

                <!-- Content when loaded -->
                <div *ngIf="!loading && currentIP" class="space-y-6">
                    <!-- Geolocation Information -->
                    <mat-card *ngIf="geolocation" class="bg-blue-50 dark:bg-blue-900">
                        <mat-card-content class="p-4">
                            <div class="flex items-start gap-3 mb-3">
                                <mat-icon class="text-blue-600 dark:text-blue-400">location_on</mat-icon>
                                <div class="flex-1">
                                    <h3 class="text-lg font-semibold text-blue-800 dark:text-blue-200 mb-3">
                                        Información Geográfica
                                    </h3>

                                    <!-- Map -->
                                    <div class="mb-4 bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow-md">
                                        <app-geolocation-map
                                            [latitude]="geolocation.latitude"
                                            [longitude]="geolocation.longitude"
                                            [city]="geolocation.city"
                                            [country]="geolocation.country"
                                            [zoom]="10"
                                            [height]="'300px'">
                                        </app-geolocation-map>
                                    </div>

                                    <!-- Location -->
                                    <div class="mb-3">
                                        <p class="text-xs font-semibold text-secondary mb-1">Ubicación</p>
                                        <p class="text-base font-bold">
                                            <span *ngIf="geolocation.flag" class="mr-2">{{ geolocation.flag }}</span>
                                            {{ geolocation.city }}, {{ geolocation.country }}
                                        </p>
                                        <p class="text-sm text-secondary">{{ geolocation.region }}</p>
                                    </div>

                                    <!-- ISP -->
                                    <div class="mb-3">
                                        <p class="text-xs font-semibold text-secondary mb-1">Proveedor (ISP)</p>
                                        <p class="text-base font-bold">{{ geolocation.isp }}</p>
                                        <p class="text-sm text-secondary">{{ geolocation.org }}</p>
                                    </div>

                                    <!-- Coordinates & Timezone -->
                                    <div>
                                        <p class="text-xs font-semibold text-secondary mb-1">Coordenadas</p>
                                        <p class="text-sm font-mono">{{ geolocation.latitude }}, {{
                                            geolocation.longitude }}</p>
                                        <p class="text-xs font-semibold text-secondary mb-1 mt-2">Zona Horaria</p>
                                        <p class="text-sm">{{ geolocation.timezone }}</p>
                                        <mat-chip *ngIf="geolocation.isEU" class="mt-2 text-xs" color="primary">
                                            Unión Europea
                                        </mat-chip>
                                    </div>
                                </div>
                            </div>
                        </mat-card-content>
                    </mat-card>

                    <!-- Clean IP Message -->
                    <mat-card *ngIf="iocResults.length === 0 && !errorMessage" class="bg-green-50 dark:bg-green-900">
                        <mat-card-content class="p-4">
                            <div class="flex items-start gap-3">
                                <mat-icon class="text-green-600 dark:text-green-400">shield_check</mat-icon>
                                <div class="flex-1">
                                    <h3 class="text-lg font-semibold text-green-800 dark:text-green-200 mb-2">IP Limpia
                                    </h3>
                                    <p class="text-sm text-green-700 dark:text-green-300">
                                        No se encontró información de amenazas para <strong>{{ currentIP }}</strong>.
                                    </p>
                                    <p class="text-sm text-green-700 dark:text-green-300 mt-1">
                                        Esta IP no aparece en las bases de datos de amenazas consultadas. Esto es una buena señal.
                                    </p>
                                </div>
                            </div>
                        </mat-card-content>
                    </mat-card>

                    <!-- API Key Expired Error -->
                    <mat-card *ngIf="apiKeyExpired" class="bg-orange-50 dark:bg-orange-900 border-2 border-orange-300 dark:border-orange-700">
                        <mat-card-content class="p-4">
                            <div class="flex items-start gap-3">
                                <mat-icon class="text-orange-600 dark:text-orange-400">key_off</mat-icon>
                                <div class="flex-1">
                                    <h3 class="text-lg font-semibold text-orange-800 dark:text-orange-200 mb-2">
                                        Clave API Expirada
                                    </h3>
                                    <p class="text-sm text-orange-700 dark:text-orange-300 mb-2">
                                        La clave API de Blackbox ha expirado. No se puede verificar si <strong>{{ currentIP }}</strong> está en bases de datos de amenazas.
                                    </p>
                                    <p class="text-sm text-orange-700 dark:text-orange-300">
                                        ⚠️ <strong>Importante:</strong> La IP podría o no ser maliciosa. Se requiere renovar la clave API para obtener información actualizada.
                                    </p>
                                </div>
                            </div>
                        </mat-card-content>
                    </mat-card>

                    <!-- General Error Message -->
                    <mat-card *ngIf="errorMessage && !apiKeyExpired" class="bg-red-50 dark:bg-red-900">
                        <mat-card-content class="p-4">
                            <div class="flex items-start gap-3">
                                <mat-icon class="text-red-600 dark:text-red-400">error</mat-icon>
                                <div class="flex-1">
                                    <h3 class="text-lg font-semibold text-red-800 dark:text-red-200 mb-2">
                                        Error al Consultar Amenazas
                                    </h3>
                                    <p class="text-sm text-red-700 dark:text-red-300 mb-2">{{ errorMessage }}</p>
                                    <p class="text-sm text-red-700 dark:text-red-300">
                                        ⚠️ <strong>Importante:</strong> No se pudo verificar si <strong>{{ currentIP }}</strong> es maliciosa. Contacta al administrador del sistema.
                                    </p>
                                </div>
                            </div>
                        </mat-card-content>
                    </mat-card>

                    <!-- IOC Results -->
                    <div *ngIf="iocResults.length > 0">
                        <h3 class="text-lg font-bold mb-3 text-warn">
                            ⚠️ {{ iocResults.length }} Amenaza(s) Detectada(s)
                        </h3>

                        <!-- IOC Cards -->
                        <mat-card *ngFor="let ioc of iocResults" class="mb-4">
                            <mat-card-header class="pb-3">
                                <div class="flex items-center justify-between w-full">
                                    <div class="flex items-center gap-3">
                                        <mat-icon class="text-warn">warning</mat-icon>
                                        <div>
                                            <mat-card-title class="text-base">{{ ioc.value }}</mat-card-title>
                                            <mat-card-subtitle class="text-xs">Fuente: {{ ioc.source
                                                }}</mat-card-subtitle>
                                        </div>
                                    </div>
                                    <mat-chip [color]="getSeverityColor(getSeverity(ioc))">
                                        <span class="text-xs font-semibold">{{ getSeverityLabel(getSeverity(ioc))
                                            }}</span>
                                    </mat-chip>
                                </div>
                            </mat-card-header>

                            <mat-card-content>
                                <!-- Threat Types -->
                                <div class="mb-3">
                                    <p class="text-xs font-semibold text-secondary mb-2">Tipos de Amenaza:</p>
                                    <div class="flex flex-wrap gap-1">
                                        <mat-chip *ngFor="let threat of ioc.threat_types" color="warn" class="text-xs">
                                            <mat-icon class="icon-size-4">{{ getThreatIcon(threat) }}</mat-icon>
                                            <span class="ml-1">{{ threat }}</span>
                                        </mat-chip>
                                    </div>
                                </div>

                                <!-- Metadata -->
                                <div class="grid grid-cols-2 gap-2 mb-3">
                                    <div class="bg-gray-50 dark:bg-gray-800 p-2 rounded">
                                        <p class="text-xs font-semibold text-secondary mb-1">Confianza</p>
                                        <p class="text-lg font-bold">{{ ioc.confidence }}</p>
                                    </div>
                                    <div class="bg-gray-50 dark:bg-gray-800 p-2 rounded">
                                        <p class="text-xs font-semibold text-secondary mb-1">Prioridad</p>
                                        <p class="text-lg font-bold">{{ ioc.priority }}/10</p>
                                    </div>
                                    <div class="bg-gray-50 dark:bg-gray-800 p-2 rounded">
                                        <p class="text-xs font-semibold text-secondary mb-1">Primera Detección</p>
                                        <p class="text-xs">{{ formatDate(ioc.first_seen) }}</p>
                                    </div>
                                    <div class="bg-gray-50 dark:bg-gray-800 p-2 rounded">
                                        <p class="text-xs font-semibold text-secondary mb-1">Última Detección</p>
                                        <p class="text-xs">{{ formatDate(ioc.last_seen) }}</p>
                                    </div>
                                </div>

                                <!-- Tags -->
                                <div>
                                    <p class="text-xs font-semibold text-secondary mb-1">Etiquetas:</p>
                                    <div class="flex flex-wrap gap-1">
                                        <mat-chip *ngFor="let tag of ioc.tags.slice(0, 10)" class="text-xs">
                                            {{ tag }}
                                        </mat-chip>
                                        <mat-chip *ngIf="ioc.tags.length > 10" class="text-xs">
                                            +{{ ioc.tags.length - 10 }} más
                                        </mat-chip>
                                    </div>
                                </div>
                            </mat-card-content>
                        </mat-card>
                    </div>
                </div>
            </div>
        </div>
    </mat-drawer>
</mat-drawer-container>